/**
 * Authentication Service
 *
 * Manages authentication tokens using secure Capacitor Preferences storage.
 * Handles token lifecycle: save, retrieve, validate, and clear.
 */

import * as preferences from '@/services/storage/preferences'
import type { AuthToken, WorkerProfile } from '@/types/auth'
import { STORAGE_KEYS, TOKEN_REFRESH_THRESHOLD_DAYS } from '@/utils/constants'
import { fetchAuthToken } from '@/services/api/auth'

/**
 * Save authentication token to secure storage
 *
 * @param authToken - Token object containing token, expiration, and worker info
 * @throws Error if storage operation fails
 */
export async function saveToken(authToken: AuthToken): Promise<void> {
    try {
        await preferences.setObject<AuthToken>(STORAGE_KEYS.AUTH_TOKEN, authToken)
        if (import.meta.env.DEV) {
            console.log('Auth token saved successfully for worker:', authToken.workerId)
        }
    } catch (error) {
        console.error('Failed to save auth token', error)
        throw error
    }
}

/**
 * Retrieve authentication token from secure storage
 *
 * @returns AuthToken object or null if no token exists
 */
export async function getToken(): Promise<AuthToken | null> {
    try {
        const token = await preferences.getObject<AuthToken>(STORAGE_KEYS.AUTH_TOKEN)
        if (import.meta.env.DEV) {
            if (token) {
                console.log('Auth token retrieved for worker:', token.workerId)
            } else {
                console.log('No auth token found in storage')
            }
        }
        return token
    } catch (error) {
        console.error('Failed to retrieve auth token', error)
        throw error
    }
}

/**
 * Clear authentication token from secure storage
 *
 * Used during logout or when token is invalidated
 */
export async function clearToken(): Promise<void> {
    try {
        await preferences.remove(STORAGE_KEYS.AUTH_TOKEN)
        if (import.meta.env.DEV) {
            console.log('Auth token cleared from storage')
        }
    } catch (error) {
        console.error('Failed to clear auth token', error)
        throw error
    }
}

/**
 * Check if token is expired
 *
 * @param token - AuthToken to check
 * @returns true if token is expired, false otherwise
 */
export function isTokenExpired(token: AuthToken): boolean {
    const expirationDate = new Date(token.expiresAt)
    const now = new Date()
    const isExpired = expirationDate <= now

    if (import.meta.env.DEV && isExpired) {
        console.log('Token expired on:', token.expiresAt)
    }

    return isExpired
}

/**
 * Check if token needs refresh
 *
 * @param token - AuthToken to check
 * @returns true if token expires in less than 7 days
 */
export function needsRefresh(token: AuthToken): boolean {
    const expirationDate = new Date(token.expiresAt)
    const now = new Date()
    const daysRemaining = (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)

    const shouldRefresh = daysRemaining < TOKEN_REFRESH_THRESHOLD_DAYS

    if (import.meta.env.DEV && shouldRefresh) {
        console.log(`Token needs refresh: ${Math.floor(daysRemaining)} days remaining`)
    }

    return shouldRefresh
}

/**
 * Check if a valid (non-expired) token exists
 *
 * @returns true if valid token exists, false otherwise
 */
export async function hasValidToken(): Promise<boolean> {
    try {
        const token = await getToken()

        if (!token) {
            if (import.meta.env.DEV) {
                console.log('Token validation failed: No token found')
            }
            return false
        }

        if (isTokenExpired(token)) {
            if (import.meta.env.DEV) {
                console.log('Token validation failed: Token expired')
            }
            return false
        }

        if (import.meta.env.DEV) {
            console.log('Valid token found for worker:', token.workerId)
        }
        return true
    } catch (error) {
        console.error('Failed to validate token', error)
        return false
    }
}

/**
 * Get worker profile from stored token
 *
 * Returns null if token is missing or expired (clears expired tokens automatically)
 * @returns WorkerProfile object or null if no valid token exists
 */
export async function getWorkerProfile(): Promise<WorkerProfile | null> {
    try {
        const token = await getToken()

        if (!token) {
            if (import.meta.env.DEV) {
                console.log('Cannot retrieve worker profile: No token found')
            }
            return null
        }

        if (isTokenExpired(token)) {
            if (import.meta.env.DEV) {
                console.log('Token expired, clearing stale token for worker:', token.workerId)
            }
            await clearToken()
            return null
        }

        const profile: WorkerProfile = {
            workerId: token.workerId,
            name: token.name,
            avatar: token.avatar,
            bx_avatar: token.bx_avatar
        }

        if (import.meta.env.DEV) {
            console.log('Worker profile retrieved:', profile.workerId)
        }
        return profile
    } catch (error) {
        console.error('Failed to retrieve worker profile', error)
        return null
    }
}

/**
 * Refresh authentication token if it is nearing expiry
 *
 * - Expired token: clears it and returns null (user must re-authenticate)
 * - Token nearing expiry (< 7 days): fetches a fresh token from the API
 * - Valid token: returns the current profile unchanged
 *
 * @returns WorkerProfile if token is still valid or was refreshed, null if expired/missing
 */
export async function refreshTokenIfNeeded(): Promise<WorkerProfile | null> {
    try {
        const token = await getToken()

        if (!token) {
            return null
        }

        if (isTokenExpired(token)) {
            if (import.meta.env.DEV) {
                console.log('Token expired during refresh check, clearing for worker:', token.workerId)
            }
            await clearToken()
            return null
        }

        if (needsRefresh(token)) {
            try {
                const tokenResponse = await fetchAuthToken(token.workerId)
                const newToken: AuthToken = {
                    token: tokenResponse.token,
                    expiresAt: tokenResponse.expiresAt,
                    workerId: tokenResponse.worker.workerId,
                    name: tokenResponse.worker.name,
                    avatar: tokenResponse.worker.avatar,
                    bx_avatar: tokenResponse.worker.bx_avatar
                }
                await saveToken(newToken)

                if (import.meta.env.DEV) {
                    console.log('Token refreshed successfully for worker:', newToken.workerId)
                }

                return {
                    workerId: newToken.workerId,
                    name: newToken.name,
                    avatar: newToken.avatar,
                    bx_avatar: newToken.bx_avatar
                }
            } catch (refreshError) {
                console.error('Failed to refresh token, continuing with current token', refreshError)
                // Return current profile - token is still valid, just couldn't refresh
                return {
                    workerId: token.workerId,
                    name: token.name,
                    avatar: token.avatar,
                    bx_avatar: token.bx_avatar
                }
            }
        }

        return {
            workerId: token.workerId,
            name: token.name,
            avatar: token.avatar,
            bx_avatar: token.bx_avatar
        }
    } catch (error) {
        console.error('Failed to check token refresh', error)
        return null
    }
}
